import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import axios from "axios";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";

const HEADER_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade8} 100%)`;
const MODAL_RADIUS = 16;

const MODAL_STYLES = `
  .mdl-edit-class-fee.modal-dialog {
    margin: 0.75rem auto 1.25rem;
    max-height: calc(100vh - 1.5rem);
    display: flex;
    align-items: center;
  }
  .mdl-edit-class-fee .modal-content {
    border: none;
    border-radius: ${MODAL_RADIUS}px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
    max-height: calc(100vh - 1.5rem);
    display: flex;
    flex-direction: column;
  }
  .mdl-edit-class-fee .modal-header {
    border-top-left-radius: ${MODAL_RADIUS}px;
    border-top-right-radius: ${MODAL_RADIUS}px;
    flex-shrink: 0;
  }
  .mdl-edit-class-fee .modal-body {
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 0;
  }
  .mdl-edit-class-fee .modal-footer {
    border-bottom-left-radius: ${MODAL_RADIUS}px;
    border-bottom-right-radius: ${MODAL_RADIUS}px;
    flex-shrink: 0;
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: ${COLORS.white};
    box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.08);
  }
  .mdl-edit-class-fee .month-fee-input:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(2, 136, 209, 0.18);
    border-color: ${COLORS.primary_color_shade6} !important;
  }
  .mdl-edit-class-fee .btn-save-class-fee {
    color: ${COLORS.white} !important;
    background: linear-gradient(135deg, ${COLORS.primary_color_shade5}, ${COLORS.primary_color_shade8}) !important;
    border: none !important;
  }
  .mdl-edit-class-fee .btn-save-class-fee:disabled {
    color: ${COLORS.white} !important;
    opacity: 0.65;
  }
`;

const MONTH_SHORT = {
  1: "Jan", 2: "Feb", 3: "Mar", 4: "Apr", 5: "May", 6: "Jun",
  7: "Jul", 8: "Aug", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dec",
};

const monthRowGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "8px",
};

function parseMonthKey(monthKey) {
  const parts = String(monthKey || "").trim().split("-");
  if (parts.length < 2) {
    return { monthLabel: monthKey || "—", year: "", monthNum: "", shortKey: monthKey || "—" };
  }
  const year = parts[0];
  const monthNum = String(parseInt(parts[1], 10)).padStart(2, "0");
  const month = parseInt(parts[1], 10);
  const shortYear = year.length >= 2 ? year.slice(-2) : year;
  return {
    monthLabel: MONTH_SHORT[month] || monthNum,
    year,
    monthNum,
    shortKey: `${shortYear}-${monthNum}`,
  };
}

const compactInputStyle = {
  fontSize: "12px",
  fontWeight: 700,
  padding: "4px 6px",
  height: "28px",
  borderRadius: "6px",
  border: `1px solid ${COLORS.green_dark}`,
  background: `linear-gradient(135deg, ${COLORS.green_light}, ${COLORS.green0})`,
  color: COLORS.leaf_green,
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
};

const compactReadmInputStyle = {
  ...compactInputStyle,
  border: `1px solid ${COLORS.card_green}`,
  background: `linear-gradient(135deg, ${COLORS.green_light}, ${COLORS.green1})`,
};

function formatINR(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function getMonthLabel(monthKey) {
  const parts = String(monthKey || "").split("-");
  if (parts.length < 2) return monthKey;
  const month = parseInt(parts[1], 10);
  return MONTH_SHORT[month] || parts[1];
}

function buildMonthEditState(feeItem, isSpecialFee) {
  const rows = Array.isArray(feeItem?.months) ? feeItem.months : [];
  return rows.map((row) => ({
    month: row.month,
    fee_value: String(row.fee_value ?? ""),
    fee_value_old: String(row.fee_value ?? ""),
    fee_value_readmission: String(
      row.readm_fee_value ?? feeItem?.fee_value_for_existing_student ?? ""
    ),
    fee_value_readmission_old: String(
      row.readm_fee_value ?? feeItem?.fee_value_for_existing_student ?? ""
    ),
    is_locked: Boolean(row.is_locked),
    can_edit: row.can_edit !== false && !row.is_locked,
    lock_reason: row.lock_reason || "",
    collected_amount: Number(row.collected_amount || 0),
    settled_amount: Number(row.settled_amount || 0),
    is_new: false,
  }));
}

function getSessionMonthsFromStartDate(startDate) {
  if (!startDate) return [];
  const temp = String(startDate).split("-");
  if (temp.length < 3) return [];
  const dateObj = new Date(Number(temp[0]), Number(temp[1]) - 1, Number(temp[2]) + 1);
  const months = [];
  for (let i = 1; i <= 12; i += 1) {
    if (i !== 1) dateObj.setMonth(dateObj.getMonth() + 1);
    const month = dateObj.getUTCMonth() + 1;
    const year = dateObj.getUTCFullYear();
    months.push(`${year}-${month < 10 ? `0${month}` : month}`);
  }
  return months;
}

function getDefaultFeeValues(feeItem, isSpecialFee, monthRows) {
  const editable = monthRows.find((row) => row.can_edit);
  if (editable) {
    return {
      fee_value: editable.fee_value || String(feeItem?.fee_value ?? "0"),
      fee_value_readmission:
        editable.fee_value_readmission ||
        String(feeItem?.fee_value_for_existing_student ?? "0"),
    };
  }
  return {
    fee_value: String(feeItem?.fee_value ?? "0"),
    fee_value_readmission: String(feeItem?.fee_value_for_existing_student ?? "0"),
  };
}

function createNewMonthRow(month, feeItem, isSpecialFee, monthRows) {
  const defaults = getDefaultFeeValues(feeItem, isSpecialFee, monthRows);
  return {
    month,
    fee_value: defaults.fee_value,
    fee_value_old: "",
    fee_value_readmission: defaults.fee_value_readmission,
    fee_value_readmission_old: "",
    is_locked: false,
    can_edit: true,
    lock_reason: "",
    collected_amount: 0,
    settled_amount: 0,
    is_new: true,
  };
}

function mergeFreshLockState(currentRows, freshFee) {
  const freshMonths = Array.isArray(freshFee?.months) ? freshFee.months : [];
  const lockByMonth = {};
  freshMonths.forEach((row) => {
    lockByMonth[row.month] = row;
  });

  return currentRows.map((row) => {
    const fresh = lockByMonth[row.month];
    if (!fresh) return row;
    return {
      ...row,
      is_locked: Boolean(fresh.is_locked),
      can_edit: row.is_new ? true : fresh.can_edit !== false && !fresh.is_locked,
      lock_reason: fresh.lock_reason || "",
      collected_amount: Number(fresh.collected_amount || 0),
      settled_amount: Number(fresh.settled_amount || 0),
    };
  });
}

async function fetchFreshFeeItem(classId, sessionId, feeId) {
  const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getClassFee.php`;
  const res = await axios.get(URL, {
    params: {
      school_code: SessionManager.getSchoolCode(),
      class_id: classId,
      session_id: sessionId,
    },
  });

  if (res.data?.api_status !== "OK") {
    throw new Error(res.data?.api_remark || "Failed to verify fee availability");
  }

  const fee = (res.data.data || []).find((item) => String(item.fee_id) === String(feeId));
  if (!fee) {
    throw new Error("Fee record not found. Please refresh and try again.");
  }

  return fee;
}

function getLockLabel(lockReason) {
  if (lockReason === "collected,settled") return "Collected & Settled";
  if (lockReason === "collected") return "Collected";
  if (lockReason === "settled") return "Settled";
  return "Locked";
}

function SectionCard({ title, children, style }) {
  return (
    <div
      style={{
        background: COLORS.white,
        border: `1px solid ${COLORS.grey3}`,
        borderRadius: "8px",
        padding: "10px 12px",
        marginBottom: "10px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        ...style,
      }}
    >
      {title && (
        <div
          style={{
            fontSize: "10px",
            fontWeight: 800,
            color: COLORS.black8,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginBottom: "8px",
            paddingBottom: "6px",
            borderBottom: `1px solid ${COLORS.grey2}`,
          }}
        >
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

function MonthStatusDot({ locked, lockReason }) {
  const title = locked ? getLockLabel(lockReason) : "Editable";
  const style = {
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: "8px",
  };

  if (locked) {
    return (
      <span
        title={title}
        style={{
          ...style,
          background: COLORS.amber1,
          color: COLORS.amber9,
          border: `1px solid ${COLORS.amber4}`,
        }}
      >
        <i className="fa fa-lock" aria-hidden="true" />
      </span>
    );
  }

  return (
    <span
      title={title}
      style={{
        ...style,
        background: COLORS.green_light,
        color: COLORS.leaf_green,
        border: `1px solid ${COLORS.green_dark}`,
      }}
    >
      <i className="fa fa-pencil" aria-hidden="true" />
    </span>
  );
}

function CompactAmount({ label, value, variant, locked }) {
  const isReadm = variant === "readm";
  const amountStyle = locked
    ? {
        color: COLORS.black7,
        background: isReadm ? COLORS.grey1 : COLORS.grey0,
        border: `1px solid ${COLORS.grey3}`,
      }
    : {
        color: COLORS.leaf_green,
        background: isReadm
          ? `linear-gradient(135deg, ${COLORS.green_light}, ${COLORS.green1})`
          : `linear-gradient(135deg, ${COLORS.green_light}, ${COLORS.green0})`,
        border: `1px solid ${isReadm ? COLORS.card_green : COLORS.green_dark}`,
      };

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        fontSize: "11px",
        fontWeight: 800,
        padding: "4px 5px",
        borderRadius: "6px",
        textAlign: "center",
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        ...amountStyle,
      }}
      title={label ? `${label}: ${formatINR(value)}` : formatINR(value)}
    >
      {label && (
        <span style={{ fontSize: "8px", fontWeight: 700, color: COLORS.black6, marginRight: "3px" }}>
          {label}
        </span>
      )}
      {formatINR(value)}
    </div>
  );
}

function CompactField({ label, value, onChange, readm }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      {label && (
        <div
          style={{
            fontSize: "8px",
            fontWeight: 700,
            color: COLORS.black6,
            textTransform: "uppercase",
            marginBottom: "2px",
            textAlign: "center",
          }}
        >
          {label}
        </div>
      )}
      <Form.Control
        type="number"
        className="month-fee-input"
        value={value}
        onChange={onChange}
        style={readm ? compactReadmInputStyle : compactInputStyle}
      />
    </div>
  );
}

function MonthEditRow({ row, isSpecialFee, onChange, onRemove, showRemove }) {
  const locked = !row.can_edit;
  const { monthLabel, shortKey } = parseMonthKey(row.month);
  const borderColor = locked ? COLORS.amber3 : COLORS.primary_color_shade3;
  const headerBg = locked ? COLORS.amber0 : COLORS.primary_color_shade0;

  return (
    <div
      title={row.month}
      style={{
        borderRadius: "8px",
        overflow: "hidden",
        background: COLORS.white,
        border: `1px solid ${borderColor}`,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "6px",
          padding: "5px 7px",
          background: headerBg,
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: "5px", minWidth: 0 }}>
          <span
            style={{
              fontSize: "14px",
              fontWeight: 800,
              color: locked ? COLORS.red7 : COLORS.primary_color_shade9,
              lineHeight: 1,
            }}
          >
            {monthLabel}
          </span>
          {row.is_new && (
            <span
              style={{
                fontSize: "8px",
                fontWeight: 700,
                padding: "1px 5px",
                borderRadius: "4px",
                background: COLORS.primary_color_shade0,
                color: COLORS.primary_color_shade8,
              }}
            >
              New
            </span>
          )}
          <span
            style={{
              fontSize: "9px",
              fontWeight: 600,
              color: COLORS.black6,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {shortKey}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
          <MonthStatusDot locked={locked} lockReason={row.lock_reason} />
          {showRemove && !locked && onRemove && (
            <button
              type="button"
              title="Remove month"
              onClick={() => onRemove(row.month)}
              style={{
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                border: `1px solid ${COLORS.red3}`,
                background: COLORS.red0,
                color: COLORS.red7,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: "6px 7px 7px" }}>
        {locked ? (
          <>
            <div style={{ display: "flex", gap: "4px" }}>
              {isSpecialFee ? (
                <>
                  <CompactAmount label="N" value={row.fee_value} locked />
                  <CompactAmount label="R" value={row.fee_value_readmission} variant="readm" locked />
                </>
              ) : (
                <CompactAmount value={row.fee_value} locked />
              )}
            </div>
            {(row.collected_amount > 0 || row.settled_amount > 0) && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "3px",
                  marginTop: "4px",
                }}
              >
                {row.collected_amount > 0 && (
                  <span
                    style={{
                      fontSize: "8px",
                      fontWeight: 700,
                      padding: "1px 5px",
                      borderRadius: "4px",
                      background: COLORS.primary_color_shade0,
                      color: COLORS.primary_color_shade8,
                    }}
                  >
                    Collected {formatINR(row.collected_amount)}
                  </span>
                )}
                {row.settled_amount > 0 && (
                  <span
                    style={{
                      fontSize: "8px",
                      fontWeight: 700,
                      padding: "1px 5px",
                      borderRadius: "4px",
                      background: COLORS.amber1,
                      color: COLORS.amber9,
                    }}
                  >
                    Set {formatINR(row.settled_amount)}
                  </span>
                )}
              </div>
            )}
          </>
        ) : (
          <div style={{ display: "flex", gap: "4px" }}>
            {isSpecialFee ? (
              <>
                <CompactField
                  label="New"
                  value={row.fee_value}
                  onChange={(e) => onChange(row.month, "fee_value", e.target.value)}
                />
                <CompactField
                  label="Readm"
                  value={row.fee_value_readmission}
                  readm
                  onChange={(e) => onChange(row.month, "fee_value_readmission", e.target.value)}
                />
              </>
            ) : (
              <CompactField
                value={row.fee_value}
                onChange={(e) => onChange(row.month, "fee_value", e.target.value)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const MdlEditClassFee = ({
  feeItem,
  sessionId,
  classId,
  sectionId,
  actionClose,
  onSaved,
}) => {
  const [show, setShow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [monthRows, setMonthRows] = useState([]);
  const [originalMonths, setOriginalMonths] = useState([]);
  const [sessionMonths, setSessionMonths] = useState([]);
  const [selectedAddMonth, setSelectedAddMonth] = useState("");

  const isSpecialFee = String(feeItem?.is_special_fee) === "1";

  const editableRows = useMemo(
    () => monthRows.filter((row) => row.can_edit),
    [monthRows]
  );

  const lockedRows = useMemo(
    () => monthRows.filter((row) => !row.can_edit),
    [monthRows]
  );

  const canDeleteFee = useMemo(
    () => lockedRows.length === 0 && monthRows.length > 0 && monthRows.every((row) => !row.is_locked),
    [lockedRows, monthRows]
  );

  const availableMonthsToAdd = useMemo(() => {
    const existing = new Set(monthRows.map((row) => row.month));
    return sessionMonths.filter((month) => !existing.has(month));
  }, [monthRows, sessionMonths]);

  const removedMonths = useMemo(
    () => originalMonths.filter((month) => !monthRows.some((row) => row.month === month)),
    [originalMonths, monthRows]
  );

  const hasChanges = useMemo(() => {
    const hasNew = monthRows.some((row) => row.is_new);
    const hasRemoved = removedMonths.length > 0;
    const hasValueChange = monthRows.some(
      (row) =>
        row.can_edit &&
        !row.is_new &&
        (row.fee_value !== row.fee_value_old ||
          (isSpecialFee && row.fee_value_readmission !== row.fee_value_readmission_old))
    );
    const hasNewValueChange = monthRows.some(
      (row) =>
        row.is_new &&
        (row.fee_value !== "" ||
          (isSpecialFee && row.fee_value_readmission !== ""))
    );
    return hasNew || hasRemoved || hasValueChange || hasNewValueChange;
  }, [monthRows, removedMonths, isSpecialFee]);

  const handleClose = useCallback(() => {
    setShow(false);
    actionClose();
  }, [actionClose]);

  useEffect(() => {
    const rows = buildMonthEditState(feeItem, isSpecialFee);
    setMonthRows(rows);
    setOriginalMonths(rows.map((row) => row.month));

    const session = SessionManager.getCurrentSession();
    const startDate = session?.[0]?.start_date || "";
    setSessionMonths(getSessionMonthsFromStartDate(startDate));
    setSelectedAddMonth("");
  }, [feeItem, isSpecialFee]);

  const handleMonthChange = (month, field, value) => {
    setMonthRows((prev) =>
      prev.map((row) => (row.month === month ? { ...row, [field]: value } : row))
    );
  };

  const handleRemoveMonth = (month) => {
    const row = monthRows.find((item) => item.month === month);
    if (!row || !row.can_edit) {
      myToast.notify_warning("This month cannot be removed");
      return;
    }
    if (monthRows.length <= 1) {
      myToast.notify_warning("At least one month is required");
      return;
    }
    setMonthRows((prev) => prev.filter((item) => item.month !== month));
  };

  const handleAddMonth = () => {
    if (!selectedAddMonth) {
      myToast.notify_warning("Select a month to add");
      return;
    }
    if (monthRows.some((row) => row.month === selectedAddMonth)) {
      myToast.notify_warning("This month is already added");
      return;
    }
    setMonthRows((prev) => [
      ...prev,
      createNewMonthRow(selectedAddMonth, feeItem, isSpecialFee, prev),
    ].sort((a, b) => a.month.localeCompare(b.month)));
    setSelectedAddMonth("");
  };

  const validateInput = () => {
    if (monthRows.length === 0) {
      myToast.notify_warning("At least one month is required");
      return false;
    }
    for (const row of editableRows) {
      if (row.fee_value === "" || row.fee_value === null) {
        myToast.notify_warning(`Enter fee value for ${getMonthLabel(row.month)}`);
        return false;
      }
      if (isSpecialFee && (row.fee_value_readmission === "" || row.fee_value_readmission === null)) {
        myToast.notify_warning(`Enter readmission fee for ${getMonthLabel(row.month)}`);
        return false;
      }
    }
    if (!hasChanges) {
      myToast.notify_warning("No changes to save");
      return false;
    }
    return true;
  };

  const buildSavePayload = () => {
    const monthUpdates = monthRows
      .filter(
        (row) =>
          row.can_edit &&
          !row.is_new &&
          (row.fee_value !== row.fee_value_old ||
            (isSpecialFee && row.fee_value_readmission !== row.fee_value_readmission_old))
      )
      .map((row) => ({
        month: row.month,
        fee_value: row.fee_value,
        fee_value_old: row.fee_value_old,
        fee_value_readmission: isSpecialFee ? row.fee_value_readmission : "0",
        fee_value_readmission_old: isSpecialFee ? row.fee_value_readmission_old : "0",
      }));

    const monthsAdded = monthRows
      .filter((row) => row.is_new)
      .map((row) => ({
        month: row.month,
        fee_value: row.fee_value,
        fee_value_readmission: isSpecialFee ? row.fee_value_readmission : "0",
      }));

    const monthsRemoved = removedMonths;

    return { monthUpdates, monthsAdded, monthsRemoved };
  };

  const handleSave = async () => {
    if (!validateInput()) return;

    const staffDetails = SessionManager.getStaffDetails();
    const staffId = staffDetails ? staffDetails.staff_id : null;
    if (!staffId) {
      myToast.notify_warning("Staff session not found. Please log in again.");
      return;
    }

    const { monthUpdates, monthsAdded, monthsRemoved } = buildSavePayload();

    if (
      monthUpdates.length === 0 &&
      monthsAdded.length === 0 &&
      monthsRemoved.length === 0
    ) {
      myToast.notify_warning("No changes to save");
      return;
    }

    setSaving(true);
    try {
      const freshFee = await fetchFreshFeeItem(classId, sessionId, feeItem.fee_id);
      const mergedRows = mergeFreshLockState(monthRows, freshFee);
      setMonthRows(mergedRows);

      const blockedUpdates = monthUpdates.filter((update) => {
        const row = mergedRows.find((item) => item.month === update.month);
        return row && !row.can_edit;
      });

      const blockedRemovals = monthsRemoved.filter((month) => {
        const freshMonths = Array.isArray(freshFee?.months) ? freshFee.months : [];
        const fresh = freshMonths.find((item) => item.month === month);
        return fresh?.is_locked;
      });

      if (blockedUpdates.length > 0 || blockedRemovals.length > 0) {
        const labels = [...blockedUpdates, ...blockedRemovals.map((m) => ({ month: m }))]
          .map((item) => getMonthLabel(item.month))
          .join(", ");
        myToast.notify_warning(
          `Cannot save: ${labels} already collected or settled. Those months are now locked.`
        );
        return;
      }

      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}updateClassFeeMonthWise.php`;
      const params = new URLSearchParams();
      params.append("staff_id", staffId);
      params.append("class_id", classId);
      params.append("session_id", sessionId);
      params.append("fee_id", feeItem.fee_id);
      params.append("is_special_fee", isSpecialFee ? "1" : "0");
      params.append("mode_id", feeItem.mode_id || "");
      params.append("school_code", SessionManager.getSchoolCode());
      params.append("month_updates", JSON.stringify(monthUpdates));
      params.append("months_added", JSON.stringify(monthsAdded));
      params.append("months_removed", JSON.stringify(monthsRemoved));

      const res = await axios.post(URL, params);
      if (res.data?.api_status === "OK") {
        myToast.notify_success("Class fee updated successfully");
        handleClose();
        if (onSaved) {
          onSaved();
        }
      } else {
        if (Array.isArray(res.data?.blocked_months) && res.data.blocked_months.length > 0) {
          const blockedMap = {};
          res.data.blocked_months.forEach((item) => {
            blockedMap[item.month] = item;
          });
          setMonthRows((prev) =>
            prev.map((row) => {
              const blocked = blockedMap[row.month];
              if (!blocked) return row;
              return {
                ...row,
                is_locked: true,
                can_edit: false,
                lock_reason: blocked.lock_reason || row.lock_reason,
                collected_amount: Number(blocked.collected_amount || row.collected_amount || 0),
                settled_amount: Number(blocked.settled_amount || row.settled_amount || 0),
              };
            })
          );
        }
        myToast.notify_warning(
          res.data?.api_remark || "Failed to update class fee — selected month may already be collected or settled"
        );
      }
    } catch (error) {
      console.error("Error updating class fee:", error);
      myToast.notify_warning(error.message || "Failed to update class fee");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFee = async () => {
    if (!canDeleteFee) {
      myToast.notify_warning("Cannot delete fee while any month is collected or settled");
      return;
    }

    const feeName = feeItem?.fee_name || "this fee";
    const confirmed = window.confirm(
      `Delete "${feeName}" for this class?\n\nThis will remove all month-wise fee records. This action cannot be undone.`
    );
    if (!confirmed) return;

    const staffDetails = SessionManager.getStaffDetails();
    const staffId = staffDetails ? staffDetails.staff_id : null;
    if (!staffId) {
      myToast.notify_warning("Staff session not found. Please log in again.");
      return;
    }

    setDeleting(true);
    try {
      const freshFee = await fetchFreshFeeItem(classId, sessionId, feeItem.fee_id);
      const freshMonths = Array.isArray(freshFee?.months) ? freshFee.months : [];
      if (freshMonths.some((row) => row.is_locked)) {
        setMonthRows(mergeFreshLockState(monthRows, freshFee));
        myToast.notify_warning("Cannot delete fee — some months are already collected or settled");
        return;
      }

      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}deleteClassFee.php`;
      const res = await axios.get(URL, {
        params: {
          staff_id: staffId,
          school_code: SessionManager.getSchoolCode(),
          class_id: classId,
          section_id: sectionId || "",
          session_id: sessionId,
          fee_id: feeItem.fee_id,
          fee_name: feeItem.fee_name || "",
          is_special_fee: isSpecialFee ? "1" : "0",
        },
      });

      if (res.data?.api_status === "OK") {
        myToast.notify_success("Class fee deleted successfully");
        handleClose();
        if (onSaved) {
          onSaved();
        }
      } else {
        myToast.notify_warning(res.data?.api_remark || "Failed to delete class fee");
      }
    } catch (error) {
      console.error("Error deleting class fee:", error);
      myToast.notify_warning(error.message || "Failed to delete class fee");
    } finally {
      setDeleting(false);
    }
  };

  const isBusy = saving || deleting;

  return (
    <>
      <style>{MODAL_STYLES}</style>
      <Modal
        show={show}
        onHide={handleClose}
        animation={false}
        size="lg"
        centered
        scrollable
        dialogClassName="mdl-edit-class-fee"
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{
            background: HEADER_GRADIENT,
            color: COLORS.white,
            borderBottom: "none",
            padding: "18px 20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
              }}
            >
              <i className="fa fa-edit" aria-hidden="true" />
            </div>
            <div>
              <Modal.Title style={{ fontSize: "18px", fontWeight: 800, margin: 0 }}>
                Edit Class Fee
              </Modal.Title>
              <div style={{ fontSize: "12px", opacity: 0.9, marginTop: "2px" }}>
                Month-wise edit — locked months cannot be changed
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body style={{ padding: "16px 20px", background: COLORS.grey0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "12px 14px",
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${COLORS.primary_color_shade0}, ${COLORS.white})`,
              border: `2px solid ${COLORS.primary_color_shade2}`,
              marginBottom: "14px",
            }}
          >
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "8px",
                background: COLORS.primary_color_shade7,
                color: COLORS.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: "15px",
                flexShrink: 0,
              }}
            >
              {(feeItem?.fee_name || "F").charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "10px", fontWeight: 700, color: COLORS.black6, textTransform: "uppercase" }}>
                Fee Name
              </div>
              <div style={{ fontSize: "16px", fontWeight: 800, color: COLORS.black9, marginTop: "2px" }}>
                {feeItem?.fee_name || "Unknown Fee"}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "10px", color: COLORS.black6, fontWeight: 700 }}>Receipt Mode</div>
              <div style={{ fontSize: "13px", fontWeight: 700, color: COLORS.primary_color_shade8 }}>
                {feeItem?.mode || "—"}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "14px" }}>
            <span style={{ fontSize: "11px", fontWeight: 700, padding: "4px 10px", borderRadius: "999px", background: COLORS.green_light, color: COLORS.leaf_green }}>
              {editableRows.length} editable
            </span>
            <span style={{ fontSize: "11px", fontWeight: 700, padding: "4px 10px", borderRadius: "999px", background: COLORS.amber1, color: COLORS.amber9 }}>
              {lockedRows.length} locked
            </span>
          </div>

          {monthRows.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px", color: COLORS.black6 }}>
              No month-wise fee data found
            </div>
          ) : (
            <>
              {(editableRows.length > 0 || availableMonthsToAdd.length > 0) && (
                <SectionCard title="Editable Months">
                  {availableMonthsToAdd.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                        marginBottom: editableRows.length > 0 ? "10px" : 0,
                        flexWrap: "wrap",
                      }}
                    >
                      <Form.Select
                        size="sm"
                        value={selectedAddMonth}
                        onChange={(e) => setSelectedAddMonth(e.target.value)}
                        style={{ flex: 1, minWidth: "140px", fontSize: "12px" }}
                      >
                        <option value="">Add month...</option>
                        {availableMonthsToAdd.map((month) => (
                          <option key={month} value={month}>
                            {getMonthLabel(month)} ({month})
                          </option>
                        ))}
                      </Form.Select>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={handleAddMonth}
                        disabled={!selectedAddMonth}
                        style={{ fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap" }}
                      >
                        <i className="fa fa-plus" aria-hidden="true" /> Add Month
                      </Button>
                    </div>
                  )}
                  {editableRows.length > 0 && (
                    <div style={monthRowGridStyle}>
                      {editableRows.map((row) => (
                        <MonthEditRow
                          key={`edit-${row.month}`}
                          row={row}
                          isSpecialFee={isSpecialFee}
                          onChange={handleMonthChange}
                          onRemove={handleRemoveMonth}
                          showRemove
                        />
                      ))}
                    </div>
                  )}
                </SectionCard>
              )}

              {lockedRows.length > 0 && (
                <SectionCard title="Locked Months" style={{ marginBottom: 0 }}>
                  <div style={monthRowGridStyle}>
                    {lockedRows.map((row) => (
                      <MonthEditRow
                        key={`locked-${row.month}`}
                        row={row}
                        isSpecialFee={isSpecialFee}
                        onChange={handleMonthChange}
                      />
                    ))}
                  </div>
                </SectionCard>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: `1px solid ${COLORS.grey3}`,
            background: COLORS.white,
            padding: "12px 20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <Button
                variant="outline-danger"
                onClick={handleDeleteFee}
                disabled={isBusy || !canDeleteFee}
                title={
                  canDeleteFee
                    ? "Delete this fee for the class"
                    : "Cannot delete — one or more months are collected or settled"
                }
                style={{
                  borderRadius: "8px",
                  padding: "8px 14px",
                  fontWeight: 700,
                  fontSize: "13px",
                  opacity: canDeleteFee ? 1 : 0.65,
                }}
              >
                {deleting ? (
                  <>
                    <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Deleting...
                  </>
                ) : (
                  <>
                    <i className="fa fa-trash" aria-hidden="true" /> Delete Fee
                  </>
                )}
              </Button>
            </div>
            <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
              <Button
                variant="light"
                onClick={handleClose}
                disabled={isBusy}
                style={{
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontWeight: 600,
                  border: `1px solid ${COLORS.grey3}`,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="btn-save-class-fee"
                onClick={handleSave}
                disabled={isBusy || !hasChanges || monthRows.length === 0}
                style={{
                  borderRadius: "8px",
                  padding: "8px 18px",
                  fontWeight: 700,
                  color: COLORS.white,
                  boxShadow: "0 4px 12px rgba(2, 119, 189, 0.3)",
                }}
              >
                {saving ? (
                  <>
                    <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check" aria-hidden="true" /> Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MdlEditClassFee;
